import { zUsdCents } from "@redotech/money/usd-amounts";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { SupportAiBillingVersion } from "@redotech/redo-billing-common/support-ai-billing-version";
import { SupportBaseBillingVersion } from "@redotech/redo-billing-common/team-billing";
import {
  DEFAULT_HUMAN_TICKET_PRICE_IN_CENTS,
  Team,
} from "@redotech/redo-model/team";
import { DEFAULT_AI_RESOLUTION_PRICE_IN_CENTS } from "@redotech/redo-support-settings-common/ai-billing";
import { CloseActionMethod } from "@redotech/redo-support-settings-common/support-settings-misc";
import { TextMessagingBillingDefault } from "@redotech/redo-support-settings-common/text-message-billing";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { groupInput, InputSchema } from "@redotech/ui/form";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { LoadTeamContext, TeamContext } from "../team";
import { SettingSection } from "./returns-package-protection/v2/component/setting-section";
import { SettingSectionTabNav } from "./returns-package-protection/v2/component/setting-section-tab-nav";
import { TabNavigationProvider } from "./returns-package-protection/v2/tab-navigation-provider";
import { AiBillingCard, aiBillingForm } from "./support/ai-billing";
import { CustomerWidgetScriptTag } from "./support/customer-widget-script-tag";
import { InboundCard, inboundForm } from "./support/inbound";
import { MigrateToSupportV2Card } from "./support/migrate-to-support-v2-card";
import { PhonesCard } from "./support/phones";
import {
  PhonesBillingCard,
  phonesBillingTextMessagingForm,
  phonesBillingVoiceForm,
} from "./support/phones-billing";
import { PlatformImportCard } from "./support/platform-import/platform-import-card";
import { ProductsCard, productsForm } from "./support/products";
import { ReconcileSupportV2Card } from "./support/reconcile-support-v2-card";
import { StorefrontAiOnboarding } from "./support/storefront-ai-onboarding";
import {
  SupportBillingCard,
  supportBillingForm,
} from "./support/support-billing";
import { SupportInvoices } from "./support/support-invoices";
import { SupportOnboarding } from "./support/support-onboarding";
import { TrueUpSupportV2Card } from "./support/true-up-support-v2-card";
import { WipeSupportV2Card } from "./support/wipe-support-v2-card";

export const SupportPage = memo(function SupportPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    void Promise.all([loadTeam(), loadPricing()]);
  });

  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [pricingLoad, loadPricing] = useTriggerLoad((signal) =>
    rpcClient.getSupportAiPricing({ teamId }, { signal }),
  );

  const value: SupportValue | undefined = useMemo(() => {
    if (!team) {
      return undefined;
    }
    if (!pricingLoad.value && !pricingLoad.error) {
      return undefined;
    }

    const billingSetting = team.settings?.support?.billing;
    const aiBilling = billingSetting?.ai || {};
    const platformPrice = pricingLoad.value?.platformPrice || 0;

    return {
      products: {
        supportEnabled: !!team.settings?.support?.enabled,
        aiEnabled: !!team.settings?.support?.ai?.enabled,
        textMessagingEnabled: !!billingSetting?.textMessaging?.enabled,
      },
      supportBilling: (() => {
        const baseBilling = team.settings.support?.billing?.base;
        const billingVersion =
          baseBilling?.version || SupportBaseBillingVersion.FLAT_RATE;
        const isSimpleUsage =
          billingVersion === SupportBaseBillingVersion.SIMPLE_HUMAN_USAGE;
        return {
          billingVersion,
          pricePerMonth: (platformPrice / 100).toString(),
          freeTrialEndDate: baseBilling?.freeTrialEndDate
            ? new Date(baseBilling.freeTrialEndDate)
            : null,
          basePercentageDiscount:
            baseBilling?.basePercentageDiscount?.toString() || "0",
          basePercentageDiscountEndDate:
            baseBilling?.basePercentageDiscountEndDate
              ? new Date(baseBilling.basePercentageDiscountEndDate)
              : null,
          baseDiscountInCents:
            baseBilling?.baseDiscountInCents?.toString() || "0",
          baseDiscountEndDate: baseBilling?.baseDiscountEndDate
            ? new Date(baseBilling.baseDiscountEndDate)
            : null,
          humanTicketPriceInCents:
            baseBilling?.humanTicketPriceInCents?.toString() ||
            (isSimpleUsage
              ? DEFAULT_HUMAN_TICKET_PRICE_IN_CENTS.toString()
              : "0"),
          pricePerOrderInCents:
            baseBilling?.pricePerOrderInCents?.toString() || "0",
        };
      })(),
      aiBilling: {
        billingVersion: (aiBilling?.version as SupportAiBillingVersion) || null,
        copilotModel: team.settings?.support?.ai?.copilotModel || null,
        freeTrialEndDate: aiBilling?.freeTrialEndDate
          ? new Date(aiBilling.freeTrialEndDate)
          : null,
        oneTimeFreeResolutions:
          aiBilling?.oneTimeFreeResolutions?.toString() || "0",
        recurringFreeResolutions:
          aiBilling?.recurringFreeResolutions?.toString() || "0",
        recurringFreeResolutionsEndDate:
          aiBilling?.recurringFreeResolutionsEndDate
            ? new Date(aiBilling.recurringFreeResolutionsEndDate)
            : null,
        resolutionPercentageDiscount:
          aiBilling?.resolutionPercentageDiscount?.toString() || "0",
        resolutionPercentageDiscountEndDate:
          aiBilling?.resolutionPercentageDiscountEndDate
            ? new Date(aiBilling.resolutionPercentageDiscountEndDate)
            : null,
        aiResolutionPriceInCents:
          aiBilling?.aiResolutionPriceInCents?.toString() ||
          (aiBilling?.version === SupportAiBillingVersion.SIMPLE_AI_USAGE
            ? DEFAULT_AI_RESOLUTION_PRICE_IN_CENTS.toString()
            : "0"),
        pricePerOrderInCents:
          aiBilling?.pricePerOrderInCents?.toString() || "0",
      },
      phonesBillingTextMessaging: {
        pricePerSMSInTenthCents: (
          (billingSetting?.textMessaging?.pricePerSMSInTenthCents || 0) / 1000
        ).toString(),
        pricePerMMSInTenthCents: (
          (billingSetting?.textMessaging?.pricePerMMSInTenthCents || 0) / 1000
        ).toString(),
        monthlyMinimumInTenthCents: (
          (billingSetting?.textMessaging?.monthlyMinimumInTenthCents || 0) /
          1000
        ).toString(),
      },
      phonesBillingVoice: {
        pricePerMinuteInCents: (
          (billingSetting?.voice?.pricePerMinuteInCents || 0) / 100
        ).toString(),
        pricePerMonthInCents: (
          (billingSetting?.voice?.pricePerMonthInCents || 0) / 100
        ).toString(),
      },
      inbound: {
        autoCloseArchivedGmailMessages:
          team.settings?.support?.autoCloseArchivedGmailMessages ?? true,
      },
    };
  }, [team, pricingLoad.value, pricingLoad.error]);

  const [apiLoading, setApiLoading] = useState(true);
  const isLoading = apiLoading || !value;

  useEffect(() => {
    const loadAll = async () => {
      setApiLoading(true);
      try {
        await Promise.all([loadTeam(), loadPricing()]);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setApiLoading(false);
      }
    };
    void loadAll();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  if (isLoading) {
    return null;
  }

  return (
    <SupportForm
      id={teamId}
      key={`${teamId}-${team?.updatedAt}`}
      onSave={handleSave}
      team={team}
      value={value}
    />
  );
});

const supportForm = groupInput({
  products: productsForm,
  supportBilling: supportBillingForm,
  aiBilling: aiBillingForm,
  phonesBillingTextMessaging: phonesBillingTextMessagingForm,
  phonesBillingVoice: phonesBillingVoiceForm,
  inbound: inboundForm,
});

export type SupportForm = InputSchema.Input<typeof supportForm>;

export type SupportValue = InputSchema.Value<typeof supportForm>;

const SupportForm = memo(function SupportForm({
  onSave,
  id,
  team,
  value,
}: {
  id: string;
  value: SupportValue;
  team: Team | undefined;
  onSave?: (value: SupportValue) => void;
}) {
  const client = useRequiredContext(RedoAdminClientContext);

  const input: SupportForm = useInput(supportForm, value);

  const [hasAiValidationErrors, setHasAiValidationErrors] = useState(false);

  const handleAiValidationChange = useCallback((hasErrors: boolean) => {
    setHasAiValidationErrors(hasErrors);
  }, []);

  const save: PageFormSave = useHandler(async (signal) => {
    if (hasAiValidationErrors) {
      throw new Error("Please fix all validation errors before saving");
    }

    const value = input.value;
    const team = await getTeam(client, { teamId: id, signal });

    team.settings.support = team.settings.support || {
      enabled: !!value.products.supportEnabled,
      ticketAssignment: "manual",
      usersForAutoAssignment: [],
      maxTicketsPerUser: "10",
      closeAction: CloseActionMethod.NEXT,
      undoSend: true,
    };

    team.settings.support.enabled = !!value.products.supportEnabled;
    team.settings.support.autoCloseArchivedGmailMessages =
      value.inbound.autoCloseArchivedGmailMessages;

    if (team.settings.support.ai) {
      team.settings.support.ai.enabled = !!value.products.aiEnabled;
    } else {
      team.settings.support.ai = {
        enabled: !!value.products.aiEnabled,
        copilotEnabled: false,
        autoGenerateResponses: true,
        hasTriedAi: false,
        useEmailSignature: false,
      };
    }

    if (input.inputs.aiBilling.inputs.copilotModel.changed) {
      team.settings.support.ai.copilotModel =
        value.aiBilling.copilotModel || undefined;
    }

    const billing = team.settings.support.billing || {};

    let aiBilling = billing.ai || {};

    if (
      input.inputs.aiBilling.inputs.billingVersion.changed ||
      input.inputs.aiBilling.inputs.freeTrialEndDate.changed ||
      input.inputs.aiBilling.inputs.oneTimeFreeResolutions.changed ||
      input.inputs.aiBilling.inputs.recurringFreeResolutions.changed ||
      input.inputs.aiBilling.inputs.recurringFreeResolutionsEndDate.changed ||
      input.inputs.aiBilling.inputs.resolutionPercentageDiscount.changed ||
      input.inputs.aiBilling.inputs.resolutionPercentageDiscountEndDate
        .changed ||
      input.inputs.aiBilling.inputs.aiResolutionPriceInCents.changed ||
      input.inputs.aiBilling.inputs.pricePerOrderInCents.changed
    ) {
      aiBilling = {
        ...aiBilling,
        version: value.aiBilling.billingVersion || undefined,
        freeTrialEndDate: value.aiBilling.freeTrialEndDate || undefined,
        oneTimeFreeResolutions:
          Number(value.aiBilling.oneTimeFreeResolutions) || 0,
        recurringFreeResolutions:
          Number(value.aiBilling.recurringFreeResolutions) || 0,
        recurringFreeResolutionsEndDate:
          value.aiBilling.recurringFreeResolutionsEndDate || undefined,
        resolutionPercentageDiscount:
          Number(value.aiBilling.resolutionPercentageDiscount) || 0,
        resolutionPercentageDiscountEndDate:
          value.aiBilling.resolutionPercentageDiscountEndDate || undefined,
        aiResolutionPriceInCents:
          value.aiBilling.aiResolutionPriceInCents !== "" &&
          value.aiBilling.aiResolutionPriceInCents !== null
            ? Number(value.aiBilling.aiResolutionPriceInCents)
            : undefined,
        pricePerOrderInCents:
          value.aiBilling.pricePerOrderInCents !== "" &&
          value.aiBilling.pricePerOrderInCents !== null
            ? zUsdCents.parse(Number(value.aiBilling.pricePerOrderInCents))
            : undefined,
        atShopifyCap: false,
        ticketsUsed: aiBilling.ticketsUsed || 0,
      };
    }

    let baseBilling = billing.base || {
      pricePerMonth: 0,
      subscriptionActive: false,
    };

    const humanTicketPriceMissingInDbForSimpleUsage =
      value.supportBilling.billingVersion ===
        SupportBaseBillingVersion.SIMPLE_HUMAN_USAGE &&
      team.settings.support?.billing?.base?.humanTicketPriceInCents == null;

    if (
      input.inputs.products.inputs.supportEnabled.changed ||
      input.inputs.supportBilling.inputs.billingVersion.changed ||
      input.inputs.supportBilling.inputs.pricePerMonth.changed ||
      input.inputs.supportBilling.inputs.freeTrialEndDate.changed ||
      input.inputs.supportBilling.inputs.basePercentageDiscount.changed ||
      input.inputs.supportBilling.inputs.basePercentageDiscountEndDate
        .changed ||
      input.inputs.supportBilling.inputs.baseDiscountInCents.changed ||
      input.inputs.supportBilling.inputs.baseDiscountEndDate.changed ||
      input.inputs.supportBilling.inputs.humanTicketPriceInCents.changed ||
      input.inputs.supportBilling.inputs.pricePerOrderInCents.changed ||
      humanTicketPriceMissingInDbForSimpleUsage
    ) {
      baseBilling = {
        ...baseBilling,
        version: value.supportBilling.billingVersion,
        pricePerMonthInCents: Number(value.supportBilling.pricePerMonth) * 100,
        freeTrialEndDate: value.supportBilling.freeTrialEndDate
          ? value.supportBilling.freeTrialEndDate
          : undefined,
        basePercentageDiscount:
          value.supportBilling.basePercentageDiscount !== ""
            ? Number(value.supportBilling.basePercentageDiscount)
            : undefined,
        basePercentageDiscountEndDate:
          value.supportBilling.basePercentageDiscountEndDate || undefined,
        baseDiscountInCents:
          value.supportBilling.baseDiscountInCents !== ""
            ? Number(value.supportBilling.baseDiscountInCents)
            : undefined,
        baseDiscountEndDate:
          value.supportBilling.baseDiscountEndDate || undefined,
        humanTicketPriceInCents:
          value.supportBilling.humanTicketPriceInCents !== ""
            ? Number(value.supportBilling.humanTicketPriceInCents)
            : undefined,
        pricePerOrderInCents:
          value.supportBilling.pricePerOrderInCents !== ""
            ? zUsdCents.parse(Number(value.supportBilling.pricePerOrderInCents))
            : undefined,
      };
    }

    if (!billing.textMessaging) {
      billing.textMessaging = { ...TextMessagingBillingDefault };
    }

    billing.textMessaging.enabled = value.products.textMessagingEnabled;
    billing.textMessaging.pricePerSMSInTenthCents =
      Number(value.phonesBillingTextMessaging.pricePerSMSInTenthCents) * 1000;
    billing.textMessaging.pricePerMMSInTenthCents =
      Number(value.phonesBillingTextMessaging.pricePerMMSInTenthCents) * 1000;
    billing.textMessaging.monthlyMinimumInTenthCents =
      Number(value.phonesBillingTextMessaging.monthlyMinimumInTenthCents) *
      1000;

    if (!billing.voice) {
      billing.voice = { subscriptionActive: false };
    }

    billing.voice.pricePerMinuteInCents =
      Number(value.phonesBillingVoice.pricePerMinuteInCents) * 100;
    billing.voice.pricePerMonthInCents =
      Number(value.phonesBillingVoice.pricePerMonthInCents) * 100;

    billing.ai = aiBilling;
    billing.base = baseBilling;
    team.settings.support.billing = billing;

    await updateTeam(client, { teamId: id, team, signal });
    onSave?.(value);
  });

  const {
    products,
    supportBilling,
    aiBilling,
    phonesBillingTextMessaging,
    phonesBillingVoice,
    inbound,
  } = input.inputs;

  return (
    <TabNavigationProvider>
      <Flex dir="row" w="full">
        <SettingSectionTabNav />
        <Flex dir="column" mx="auto" style={{ width: 696, maxWidth: "100%" }}>
          <PageForm initial={value} input={input} save={save}>
            <SettingSection
              description="Enable or disable support products"
              id="products"
              title="Products"
            >
              <ProductsCard input={products} />
            </SettingSection>

            <SettingSection
              description="Configure support billing settings"
              id="support-billing"
              title="Support Billing"
            >
              <SupportBillingCard
                billingStartDate={
                  team?.settings?.support?.billing?.base?.billingStartDate
                    ? new Date(
                        team.settings.support.billing.base.billingStartDate,
                      )
                    : null
                }
                input={supportBilling}
              />
            </SettingSection>

            <SettingSection
              description="Configure AI billing settings"
              id="ai-billing"
              title="AI Billing"
            >
              <AiBillingCard
                input={aiBilling}
                onValidationChange={handleAiValidationChange}
              />
            </SettingSection>

            <SettingSection
              description="Configure text messaging and voice billing"
              id="phones-billing"
              title="Phones Billing"
            >
              <PhonesBillingCard
                textMessagingInput={phonesBillingTextMessaging}
                voiceInput={phonesBillingVoice}
              />
            </SettingSection>

            <SettingSection
              description="Configure phone number settings"
              id="phones"
              title="Phones"
            >
              <PhonesCard teamId={id} />
            </SettingSection>

            <SettingSection
              description="Support onboarding checklist"
              id="onboarding"
              title="Onboarding"
            >
              <SupportOnboarding />
              <StorefrontAiOnboarding />
            </SettingSection>

            <SettingSection
              description="Configure inbound email behavior"
              id="inbound"
              title="Inbound"
            >
              <InboundCard input={inbound} />
            </SettingSection>

            <SettingSection
              description="Customer widget script tag configuration"
              id="widget-script"
              title="Widget Script Tag"
            >
              <CustomerWidgetScriptTag />
            </SettingSection>

            <SettingSection
              description="Import data from other platforms"
              id="platform-import"
              title="Platform Import"
            >
              <PlatformImportCard teamId={team?._id} />
            </SettingSection>

            <SettingSection
              description="Migrate all team data to support-v2"
              id="migrate-to-support-v2"
              title="Migrate to support-v2"
            >
              <MigrateToSupportV2Card teamId={id} />
            </SettingSection>

            <SettingSection
              description="Sync open v1 conversations into v2 (status/assignee/tags) and migrate any that are missing."
              id="trueup-support-v2"
              title="True-up support-v2 tickets"
            >
              <TrueUpSupportV2Card teamId={id} />
            </SettingSection>

            <SettingSection
              description="Reconcile v2 tickets back to v1: remove duplicates, close orphans matched to terminal v1, and sync legacy statuses."
              id="reconcile-support-v2"
              title="Reconcile support-v2 tickets"
            >
              <ReconcileSupportV2Card teamId={id} />
            </SettingSection>

            <SettingSection
              description="Full reset of migrated support-v2 data for clean re-migration"
              id="wipe-support-v2"
              title="Wipe support-v2 data"
            >
              <WipeSupportV2Card teamId={id} />
            </SettingSection>

            <SettingSection
              description="View support billing invoices"
              id="invoices"
              title="Invoices"
            >
              <SupportInvoices teamId={id} />
            </SettingSection>
          </PageForm>
        </Flex>
      </Flex>
    </TabNavigationProvider>
  );
});
